import type { JSX } from 'react'
import NextImage, { ImageProps as NextImageProps, ImageLoaderProps } from 'next/image'

type CustomNextImageProps = Omit<NextImageProps, 'alt' | 'key'> & {
  key?: string | number | null | undefined
  alt?: string
}

export function cloudinaryLoader({ src, width, quality }: ImageLoaderProps): string {
  const params = ['c_limit', 'w_' + width, 'q_' + (quality || 'auto'), 'f_webp']
  return `https://images.angelstudios.com/image/upload/${params.join(',')}${src}`
}

const isContentfulImage = (src: string): boolean => {
  return src.includes('images.ctfassets.net')
}

const contentfulLoader = ({ src, width, quality = 100 }: ImageLoaderProps): string => {
  const rawSrc = src.split('?')[0]
  return `${rawSrc}?w=${width}&q=${quality}&fm=webp`
}

const remoteImageLoader = ({ src }: ImageLoaderProps): string => {
  return src
}

const getLoader = (isFullyQualified: boolean, isContentful: boolean) => {
  if (isContentful) {
    return contentfulLoader
  }
  if (isFullyQualified) {
    return remoteImageLoader
  }
  return cloudinaryLoader
}

export const Image = ({ src, alt = '', ...props }: CustomNextImageProps): JSX.Element | null => {
  if (!src) return null

  const isSrcString = typeof src === 'string'
  if (!isSrcString) {
    return <NextImage src={src} alt={alt} {...props} />
  }
  const isFullyQualified = src.startsWith('http://') || src.startsWith('https://')
  const hasForwardSlash = src.startsWith('/')
  const shouldAddForwardSlash = !isFullyQualified && !hasForwardSlash
  const internalSrc = shouldAddForwardSlash ? `/${src}` : src
  const isContentful = isContentfulImage(src)
  const loader = getLoader(isFullyQualified, isContentful)

  return (
    <NextImage unoptimized={isFullyQualified && !isContentful} src={internalSrc} loader={loader} alt={alt} {...props} />
  )
}
